"use client";

import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@/components/ui";

const HEADER_OFFSET = 120;

type TocLink = { id: string; label: string };

export function TableOfContentsScrollSpy({ links }: { links: readonly TocLink[] }) {
  const [activeId, setActiveId] = useState<string | null>(links[0]?.id ?? null);
  const intersectingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const ids = links.map((l) => l.id);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (ids.includes(entry.target.id)) {
            if (entry.isIntersecting) {
              intersectingRef.current.add(entry.target.id);
            } else {
              intersectingRef.current.delete(entry.target.id);
            }
          }
        });
        const visible = ids.filter((id) => intersectingRef.current.has(id));
        if (visible.length > 0) {
          setActiveId(visible[0]);
        }
      },
      {
        rootMargin: `-${HEADER_OFFSET}px 0px -70% 0px`,
        threshold: 0,
      }
    );

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el != null);

    elements.forEach((el) => observer.observe(el));
    return () => elements.forEach((el) => observer.unobserve(el));
  }, [links]);

  return (
    <nav
      aria-label="Table of contents"
      className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-5 pb-4 border-b border-gray-100">
        <div className="w-1 h-7 bg-accent rounded-full shrink-0" aria-hidden />
        <Typography variant="h3" as="h2" className="text-lg font-bold text-gray-900">
          On this page
        </Typography>
      </div>
      <ul className="space-y-2">
        {links.map(({ id, label }) => {
          const isActive = activeId === id;
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                className={`flex items-center gap-3 transition-colors group text-[14px] leading-relaxed ${
                  isActive ? "text-accent font-semibold" : "text-gray-600 hover:text-accent"
                }`}
                aria-current={isActive ? "location" : undefined}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors ${
                    isActive ? "bg-accent" : "bg-gray-300 group-hover:bg-accent"
                  }`}
                  aria-hidden
                />
                <span className={isActive ? "" : "group-hover:underline underline-offset-2"}>
                  {label}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

